import { X, Minus, Plus, ShoppingBag, Loader2, Sparkles } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CartDrawer() {
  const { items, open, setOpen, updateQuantity, removeItem, addItem, subtotal } = useCart();
  const [checking, setChecking] = useState(false);
  const [stickerAddon, setStickerAddon] = useState(null);

  useEffect(() => {
    if (open) {
      api.get('/shop/sticker-addon').then(({ data }) => {
        if (data.enabled) setStickerAddon(data);
      }).catch(() => {});
    }
  }, [open]);

  const handleCheckout = async () => {
    setChecking(true);
    try {
      const { data } = await api.post('/checkout', {
        items: items.map(i => ({ variant_id: i.variant_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Checkout failed';
      alert(detail);
    } finally {
      setChecking(false);
    }
  };

  const hasSticker = items.some(i => i.product_type === 'sticker' || i.product_name?.toLowerCase().includes('sticker'));
  const showAddon = stickerAddon && !hasSticker && items.length > 0;

  const addStickerAddon = () => {
    if (!stickerAddon) return;
    addItem({
      variant_id: stickerAddon.variant_id,
      product_id: stickerAddon.product_id,
      name: stickerAddon.product_name,
      product_name: stickerAddon.product_name,
      variant_name: stickerAddon.variant_name,
      price: stickerAddon.price,
      currency: stickerAddon.currency,
      image_url: stickerAddon.image_url ? `${API_URL}${stickerAddon.image_url}` : '',
      quantity: 1,
      product_type: 'sticker',
    });
  };

  if (!open) return null;

  return (
    <>
      <div data-testid="cart-overlay" className="fixed inset-0 bg-black/40 z-50" onClick={() => setOpen(false)} />
      <div data-testid="cart-drawer" className="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', borderLeft: '1px solid var(--border-color)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <ShoppingBag size={16} />
            <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600 }}>
              Your Cart ({items.length})
            </span>
          </div>
          <button data-testid="close-cart" onClick={() => setOpen(false)} className="p-1 transition-opacity hover:opacity-60">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <ShoppingBag size={32} style={{ opacity: 0.3 }} />
              <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', color: 'var(--muted-color)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Your cart is empty</p>
              <button onClick={() => setOpen(false)} style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--accent-color)', textTransform: 'uppercase', letterSpacing: '0.1em', background: 'none', border: 'none', cursor: 'pointer' }}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => (
                <div key={item.variant_id} className="flex gap-4 pb-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-20 h-24 object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ fontFamily: 'var(--body-font)', fontSize: '0.8rem', fontWeight: 600 }}>{item.name}</p>
                    <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', color: 'var(--muted-color)' }}>{item.variant_name}</p>
                    <p className="mt-1" style={{ fontFamily: 'var(--body-font)', fontSize: '0.8rem', fontWeight: 600 }}>${parseFloat(item.price).toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`qty-minus-${item.variant_id}`} onClick={() => updateQuantity(item.variant_id, item.quantity - 1)} className="w-7 h-7 flex items-center justify-center" style={{ border: '1px solid var(--border-color)' }}>
                        <Minus size={12} />
                      </button>
                      <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.75rem', minWidth: '1.5rem', textAlign: 'center' }}>{item.quantity}</span>
                      <button data-testid={`qty-plus-${item.variant_id}`} onClick={() => updateQuantity(item.variant_id, item.quantity + 1)} className="w-7 h-7 flex items-center justify-center" style={{ border: '1px solid var(--border-color)' }}>
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.variant_id)} className="self-start p-1 transition-opacity hover:opacity-60">
                    <X size={14} style={{ color: 'var(--muted-color)' }} />
                  </button>
                </div>
              ))}

              {/* Sticker add-on suggestion */}
              {showAddon && (
                <div data-testid="sticker-addon" className="flex items-center gap-3 p-3 rounded-lg" style={{ border: '1px dashed var(--border-color)', backgroundColor: 'var(--card-bg, transparent)' }}>
                  {stickerAddon.image_url && (
                    <img src={`${API_URL}${stickerAddon.image_url}`} alt={stickerAddon.product_name} className="w-12 h-12 object-cover rounded flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={12} style={{ color: 'var(--accent-color)' }} />
                      <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', fontWeight: 600 }}>{stickerAddon.label}</p>
                    </div>
                    <p style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', color: 'var(--muted-color)' }}>
                      {stickerAddon.product_name} — ${parseFloat(stickerAddon.price).toFixed(2)}
                    </p>
                  </div>
                  <button
                    data-testid="add-sticker-addon"
                    onClick={addStickerAddon}
                    className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider flex-shrink-0 transition-opacity hover:opacity-80"
                    style={{ backgroundColor: 'var(--accent-color)', color: '#fff', fontFamily: 'var(--body-font)', border: 'none', cursor: 'pointer' }}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-4" style={{ borderTop: '1px solid var(--border-color)' }}>
            <div className="flex justify-between mb-2">
              <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted-color)' }}>Subtotal</span>
              <span style={{ fontFamily: 'var(--body-font)', fontSize: '0.95rem', fontWeight: 700 }}>${subtotal.toFixed(2)}</span>
            </div>
            <p className="mb-4" style={{ fontFamily: 'var(--body-font)', fontSize: '0.65rem', color: 'var(--muted-color)' }}>Shipping & taxes calculated at checkout</p>
            <Button
              data-testid="checkout-button"
              className="w-full py-5 text-xs uppercase tracking-widest font-semibold"
              style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
              onClick={handleCheckout}
              disabled={checking}
            >
              {checking ? <><Loader2 size={14} className="animate-spin mr-2" /> Processing...</> : `Checkout — $${subtotal.toFixed(2)}`}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
